/**
 * One row of the inbox.
 *
 * Three things it will not do, each of which this build has paid for once:
 *
 *  1. **A withheld number is never a zero.** `measured_value` and `threshold` are
 *     `number | null`; `readingOf` turns them into a union whose withheld branch has
 *     no `.value`, so `?? 0` does not compile. The row prints the words *"không có
 *     số đo"* and no digit. (D-22.)
 *  2. **Severity is icon + text.** `Badge` requires both and offers no prop that
 *     paints a colour alone.
 *  3. **A redacted row is still a row, and it says what was withheld.** Redaction is
 *     at field level, never row level: whether an alert exists and what application
 *     it concerns are two different sensitivities, and dropping the row would make a
 *     triage queue lie about its own size.
 *
 * `group_key: null` is **withheld**, not absent — the column is NOT NULL in the
 * database and the whole key is replaced when one of its components names something
 * this caller may not see. So it renders as withheld rather than as "no subject",
 * which would read as a fleet-wide alert.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstantWithAge } from '../../i18n/datetime';
import type { MessageKey } from '../../i18n/messages';
import type { AlertSeverity, AlertSummary } from '../../api/types';
import { acknowledgeAffordance, BADGE_SEVERITY, readingOf } from '../../alerts/inbox';
import { Badge } from '../../states/Badge';
import { SEVERITY_ICON } from '../../states/icons';

const SEVERITY_LABEL: Readonly<Record<AlertSeverity, MessageKey>> = {
  informational: 'severity.info',
  low: 'severity.low',
  medium: 'severity.medium',
  high: 'severity.high',
  critical: 'severity.critical',
};

export interface AlertRowProps {
  readonly alert: AlertSummary;
  readonly timezone: string;
  readonly mayResolve: boolean;
  readonly onAcknowledge: (alert: AlertSummary) => void;
}

export function AlertRow({ alert, timezone, mayResolve, onAcknowledge }: AlertRowProps): ReactNode {
  const { t, locale } = useI18n();
  const badge = BADGE_SEVERITY[alert.severity];
  const measured = readingOf(alert.measured_value);
  const threshold = readingOf(alert.threshold);
  const affordance = acknowledgeAffordance(alert.status, alert.acknowledged_at, mayResolve);

  // The two severities an operator is expected to clear today, and only while nobody
  // has signed for them: a tinted ground plus 1px on all four sides (DESIGN.md §12).
  // Everything else in the queue rests colourless, so the tint means something.
  const wants =
    affordance === 'offer' && (badge === 'critical' || badge === 'high')
      ? ` wp-c-ruled__row--act-${badge}`
      : '';

  return (
    <li className={`wp-c-ruled__row${wants}`} data-testid={`l5-alert-${alert.id}`}>
      <div className="wp-c-ruled__main">
        {/* The severity and the sentence on one line: the badge is read as the first
            word of the sentence rather than as a label above it. */}
        <p className="wp-c-ruled__line">
          <Badge
            severity={badge}
            icon={SEVERITY_ICON[badge]}
            label={t(SEVERITY_LABEL[alert.severity])}
            testId={`l5-alert-severity-${alert.id}`}
          />
          <span>{alert.summary}</span>
        </p>
        <p className="wp-c-ruled__meta" data-testid={`l5-alert-subject-${alert.id}`}>
          <code className="wp-mono">{alert.trigger}</code>
          {' · '}
          {alert.group_key === null
            ? t('alerts.subject.withheld')
            : t('alerts.subject', { key: alert.group_key })}
          {' · '}
          {formatInstantWithAge(alert.triggered_at, locale, timezone, t('common.never'))}
          {' · '}
          {t('alerts.occurrences', { count: alert.occurrence_count })}
        </p>
        <p className="wp-c-ruled__meta" data-testid={`l5-alert-measure-${alert.id}`}>
          {measured.kind === 'known'
            ? t('alerts.measured', { value: measured.value })
            : t('alerts.measured.withheld')}
          {' · '}
          {threshold.kind === 'known'
            ? t('alerts.threshold', { value: threshold.value })
            : t('alerts.threshold.withheld')}
        </p>
        {alert.redacted ? (
          <p className="wp-small wp-muted" data-testid={`l5-alert-redacted-${alert.id}`}>
            {t('alerts.redacted')}
          </p>
        ) : null}
        {alert.incident_id === null ? null : (
          <p className="wp-small" data-testid={`l5-alert-incident-${alert.id}`}>
            {t('alerts.hasIncident', { id: alert.incident_id })}
          </p>
        )}
      </div>

      <div className="wp-c-ruled__aside">
        {affordance === 'offer' ? (
          <button
            className="wp-button"
            type="button"
            data-testid={`l5-ack-open-${alert.id}`}
            onClick={() => {
              onAcknowledge(alert);
            }}
          >
            {t('alerts.ack.open')}
          </button>
        ) : null}
        {/* No control at all in the other three branches, and never a disabled one:
            for `done` there is nothing left a button could do — the database refuses
            every UPDATE to an acknowledged row — and for `suppressed` the thing to
            act on is the other row. A greyed button would assert a permission
            problem in both cases, which is the wrong diagnosis. */}
        {affordance === 'done' ? (
          <p className="wp-small" data-testid={`l5-ack-done-${alert.id}`}>
            {t('alerts.ack.done', {
              who: alert.acknowledged_by ?? t('alerts.ack.unknownActor'),
              when: formatInstantWithAge(
                alert.acknowledged_at,
                locale,
                timezone,
                t('common.never'),
              ),
            })}
          </p>
        ) : null}
        {affordance === 'suppressed' ? (
          <p className="wp-small wp-muted" data-testid={`l5-ack-suppressed-${alert.id}`}>
            {t('alerts.ack.suppressed')}
          </p>
        ) : null}
        {affordance === 'no-permission' ? (
          <p className="wp-small wp-muted" data-testid={`l5-ack-readonly-${alert.id}`}>
            {t('alerts.ack.readOnly')}
          </p>
        ) : null}
      </div>
    </li>
  );
}
